<template>
  <div class="container-xl px-4 deposits-page">
    <div class="row">
      <div class="col-12">
        <div class="page-header-mobile d-flex justify-content-between align-items-center mb-3 mt-2">
          <h2 class="fw-bold mb-0 page-title-mobile">Gestión de Depósitos</h2>
          <button
            class="btn btn-dark d-flex align-items-center btn-mobile-primary"
            @click="openCreateModal"
            :disabled="loading"
          >
            <i class="bi bi-plus-lg me-2"></i> Nuevo Depósito
          </button>
        </div>
      </div>
    </div>

    <div class="mobile-context-chip d-lg-none">
      <span class="mobile-context-label">Catálogo e Inventario</span>
      <strong>Depósitos</strong>
    </div>

    <div class="row g-4">
      <div v-if="loading" class="col-12">
        <div class="mobile-empty-state">
          <i class="bi bi-arrow-clockwise spin"></i>
          <span>Cargando depósitos...</span>
        </div>
      </div>
      <div v-else-if="deposits.length === 0" class="col-12">
        <div class="mobile-empty-state">
          <i class="bi bi-building"></i>
          <span>No hay depósitos registrados.</span>
        </div>
      </div>
      <div class="col-12 col-md-6 col-xl-4" v-else v-for="dep in deposits" :key="dep.id">
        <div class="deposit-card border rounded p-4 h-100">
          <div class="deposit-card__top">
            <div>
              <p class="deposit-card__eyebrow">Depósito</p>
              <h4 class="fw-bold mb-0">{{ dep.nombre }}</h4>
            </div>
            <span
              :class="[
                'badge rounded-pill px-3 py-2',
                dep.activo ? 'bg-dark text-white' : 'bg-light text-dark border'
              ]"
            >
              {{ dep.activo ? 'Activo' : 'Inactivo' }}
            </span>
          </div>

          <p class="deposit-card__description">
            {{ dep.descripcion || 'Sin descripción' }}
          </p>

          <div class="deposit-card__meta">
            <div class="deposit-meta-item">
              <span class="deposit-meta-label">Actualizado</span>
              <strong>{{ formatDate(dep.actualizadoEn) }}</strong>
            </div>
            <div class="deposit-meta-item">
              <span class="deposit-meta-label">Habilitado</span>
              <strong>{{ dep.habilitado ? 'Sí' : 'No' }}</strong>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap mb-3">
            <!-- CR-DM-002. Se lee `es_mostrador`, que es la fuente de verdad,
                 con el parámetro como respaldo para las instancias que todavía
                 no tienen la columna puesta. -->
            <span v-if="dep.esMostrador || dep.id === config.deposito_mostrador_id"
                  class="badge bg-dark" data-test="badge-mostrador">Mostrador</span>
            <span v-if="dep.id === config.deposito_default_entrada" class="badge bg-secondary">Default entrada</span>
            <span v-if="dep.id === config.deposito_default_recepcion" class="badge bg-secondary">Default recepción</span>
          </div>

          <div class="deposit-card__actions">
            <button class="btn btn-outline-secondary" @click="viewDetails(dep.id)">
              Ver Detalle
            </button>
            <button class="btn btn-outline-secondary" title="Editar" @click="openEditModal(dep)">
              <i class="bi bi-pencil"></i>
            </button>
            <button class="btn btn-outline-danger" title="Eliminar" @click="deleteDeposit(dep.id)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showFormModal"
      class="modal fade show"
      style="display: block;"
      tabindex="-1"
      aria-modal="true"
      role="dialog"
      aria-labelledby="depositoFormModalLabel"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="depositoFormModalLabel">{{ isEditing ? 'Editar Depósito' : 'Nuevo Depósito' }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent>
              <input type="hidden" v-model.number="form.sucursal_id" />
              <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input
                  type="text"
                  class="form-control"
                  v-model.trim="form.nombre"
                  maxlength="120"
                  placeholder="Nombre del depósito"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Descripción</label>
                <textarea
                  class="form-control"
                  v-model.trim="form.descripcion"
                  rows="3"
                  placeholder="Descripción del depósito"
                ></textarea>
              </div>
              <div class="form-check form-switch mb-2">
                <input class="form-check-input" type="checkbox" id="depActivo" v-model="form.activo" />
                <label class="form-check-label" for="depActivo">Activo</label>
              </div>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="depHabilitado" v-model="form.habilitado" />
                <label class="form-check-label" for="depHabilitado">Habilitado</label>
              </div>
              <!-- CR-DM-002. **Acá se marca el mostrador, al crear el depósito.**
                   Antes no existía: la columna `es_mostrador` no la escribía
                   ninguna pantalla, y el operador creaba un depósito llamado
                   MOSTRADOR creyendo que alcanzaba. -->
              <hr class="my-3">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="depMostrador"
                       v-model="form.es_mostrador" data-test="deposito-es-mostrador" />
                <label class="form-check-label fw-semibold" for="depMostrador">
                  Es el depósito mostrador
                </label>
              </div>
              <div class="form-text">
                De acá salen los productos cuando se vende o se cobra. Hay uno solo por
                sucursal: marcar éste desmarca al anterior.
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline-secondary" @click="closeFormModal" :disabled="saving">Cancelar</button>
            <button class="btn btn-dark" @click="saveDeposit" :disabled="saving">
              <i v-if="saving" class="bi bi-arrow-clockwise spin me-2"></i>
              {{ isEditing ? 'Guardar cambios' : 'Crear depósito' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showFormModal" class="modal-backdrop fade show"></div>

    <div
      v-if="showDetailModal"
      class="modal fade show"
      style="display: block;"
      tabindex="-1"
      aria-modal="true"
      role="dialog"
      aria-labelledby="depositoDetailModalLabel"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="depositoDetailModalLabel">Detalle del Depósito</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="showDetailModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="detailLoading" class="text-center text-muted py-3">
              <i class="bi bi-arrow-clockwise spin me-2"></i> Cargando detalle...
            </div>
            <div v-else-if="!selectedDepositDetail">
              <div class="text-muted">No se pudo cargar el detalle.</div>
            </div>
            <div v-else class="deposit-detail-grid">
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">ID</span>
                <strong>{{ selectedDepositDetail.deposito_id }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Sucursal ID</span>
                <strong>{{ selectedDepositDetail.sucursal_id }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Nombre</span>
                <strong>{{ selectedDepositDetail.nombre }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Descripción</span>
                <strong>{{ selectedDepositDetail.descripcion || '—' }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Activo</span>
                <strong>{{ selectedDepositDetail.activo ? 'Sí' : 'No' }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Habilitado</span>
                <strong>{{ selectedDepositDetail.habilitado ? 'Sí' : 'No' }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Creado en</span>
                <strong>{{ formatDate(selectedDepositDetail.creado_en) }}</strong>
              </div>
              <div class="deposit-detail-item">
                <span class="deposit-meta-label">Actualizado en</span>
                <strong>{{ formatDate(selectedDepositDetail.actualizado_en) }}</strong>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-dark" @click="showDetailModal = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDetailModal" class="modal-backdrop fade show"></div>

    <div class="mt-4">
      <router-link class="btn btn-link px-0" :to="{ name: 'MaintenanceOperational' }">
        Configurar modo operativo de la sucursal
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import apiService from '@/services/apiService.js'
import operationalConfigService from '@/services/operationalConfigService'

const deposits = ref([])
const loading = ref(false)
const currentSucursalId = operationalConfigService.getCurrentSucursalId()
const config = ref(operationalConfigService.getConfig(currentSucursalId))

const showFormModal = ref(false)
const saving = ref(false)
const editingId = ref(null)
const isEditing = computed(() => editingId.value !== null)

const form = reactive({
  sucursal_id: null,
  nombre: '',
  descripcion: '',
  activo: true,
  habilitado: true,
  es_mostrador: false,
})

const showDetailModal = ref(false)
const detailLoading = ref(false)
const selectedDepositDetail = ref(null)

function resetForm() {
  form.sucursal_id = null
  form.nombre = ''
  form.descripcion = ''
  form.activo = true
  form.habilitado = true
  form.es_mostrador = false
}

function closeFormModal() {
  showFormModal.value = false
  editingId.value = null
  resetForm()
}

function formatDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch (e) {
    return iso
  }
}

async function loadDeposits() {
  loading.value = true
  try {
    const { data } = await apiService.get('/api/inventory/warehouses')
    const arr = Array.isArray(data?.depositos) ? data.depositos : []
    deposits.value = arr
      .map(d => ({
        id: d.deposito_id,
        sucursalId: d.sucursal_id,
        nombre: d.nombre,
        descripcion: d.descripcion,
        activo: !!d.activo,
        habilitado: !!d.habilitado,
        esMostrador: !!d.es_mostrador,
        creadoEn: d.creado_en,
        actualizadoEn: d.actualizado_en,
      }))
      .filter(d => Number(d.sucursalId) === Number(currentSucursalId))
    config.value = operationalConfigService.getConfig(currentSucursalId)
  } catch (err) {
    console.error('Error cargando depósitos', err)
  } finally {
    loading.value = false
  }
}

async function viewDetails(id) {
  showDetailModal.value = true
  detailLoading.value = true
  selectedDepositDetail.value = null
  try {
    const { data } = await apiService.get(`/api/inventory/warehouse/${id}`)
    selectedDepositDetail.value = data
  } catch (err) {
    console.error('Error cargando detalle del depósito', err)
  } finally {
    detailLoading.value = false
  }
}

function openCreateModal() {
  editingId.value = null
  resetForm()
  form.sucursal_id = currentSucursalId
  showFormModal.value = true
}

async function openEditModal(dep) {
  editingId.value = dep.id
  try {
    const { data } = await apiService.get(`/api/inventory/warehouse/${dep.id}`)
    form.sucursal_id = data.sucursal_id
    form.nombre = data.nombre
    form.descripcion = data.descripcion || ''
    form.activo = !!data.activo
    form.habilitado = !!data.habilitado
    form.es_mostrador = !!data.es_mostrador
  } catch (e) {
    form.sucursal_id = dep.sucursalId ?? null
    form.nombre = dep.nombre ?? ''
    form.descripcion = dep.descripcion ?? ''
    form.activo = !!dep.activo
    form.habilitado = !!dep.habilitado
  }
  showFormModal.value = true
}

async function saveDeposit() {
  if (!form.nombre?.trim()) {
    alert('El nombre es obligatorio')
    return
  }
  if (!form.sucursal_id || form.sucursal_id <= 0) {
    alert('Sucursal ID debe ser un número válido')
    return
  }

  const payload = {
    sucursal_id: Number(form.sucursal_id),
    nombre: form.nombre.trim(),
    descripcion: form.descripcion?.trim() || '',
    activo: !!form.activo,
    habilitado: !!form.habilitado,
    es_mostrador: !!form.es_mostrador,
  }

  saving.value = true
  try {
    const wasEditing = isEditing.value
    if (wasEditing) {
      await apiService.put(`/api/inventory/warehouse/${editingId.value}`, payload)
    } else {
      await apiService.post('/api/inventory/warehouse', payload)
    }
    await loadDeposits()
    closeFormModal()
    alert(wasEditing ? 'Depósito actualizado' : 'Depósito creado')
  } catch (err) {
    console.error('Error guardando depósito', err)
  } finally {
    saving.value = false
  }
}

async function deleteDeposit(id) {
  const confirmed = await confirm({
    message: '¿Está seguro de eliminar este depósito?',
    title: 'Eliminar depósito',
    confirmText: 'Eliminar',
    cancelText: 'Cancelar'
  })
  if (!confirmed) return
  try {
    await apiService.delete(`/api/inventory/warehouse/${id}`)
    await loadDeposits()
    alert.success('Depósito eliminado')
  } catch (err) {
    console.error('Error eliminando depósito', err)
    alert.error('No se pudo eliminar el depósito. Revisa la consola.')
  }
}

onMounted(() => {
  loadDeposits()
})
</script>

<style scoped>
.deposits-page {
  max-width: 1400px;
}

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.deposit-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-color: #e5e7eb !important;
  border-radius: 1rem !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.deposit-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
}

.deposit-card__eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.deposit-card__description {
  margin: 0;
  color: #475569;
}

.deposit-card__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
}

.deposit-meta-item,
.deposit-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.8rem 0.9rem;
  border-radius: 0.9rem;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.deposit-meta-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.deposit-card__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: auto;
}

.deposit-card__actions .btn:first-child {
  flex: 1 1 auto;
}

.deposit-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  background: #f8fafc;
  color: #64748b;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .btn-mobile-primary {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .deposits-page {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .deposit-card__top,
  .deposit-card__actions {
    flex-direction: column;
  }

  .deposit-card__meta,
  .deposit-detail-grid {
    grid-template-columns: 1fr;
  }

  .deposit-card__actions .btn {
    width: 100%;
  }
}
</style>
